import type { Point } from "highcharts"
import numeral from "numeral"
import { formatTime } from "../../../utils/date"
import { canUseNumeral } from "../../../utils/numerals"
import type { NumberAbbreviationFormat } from "../../../utils/sharedFormatter"
import { SERIE_FORMAT } from "./ChartOptions"

type Value = string | number | undefined | null

/** @deprecated */
export type FormatterOptions = {
  token?: string
  postfix?: string
  isPercentageShare?: boolean // Required for non % share -series using format: "percent"
  numberFormat?: NumberAbbreviationFormat
}

/** @deprecated */
export type Formatter<T = Point> = (formatterOptions: FormatterOptions) => (this: T) => string

/** @deprecated */
export function pricingFormatter(value: Value, format = "$0.0 a", prefix?: string) {
  if (!value) {
    return `0`
  }
  if (canUseNumeral(value)) {
    return numeral(value).format(format)
  }
  return `${prefix || "$"}${value}`.toUpperCase()
}

/** @deprecated */
export function percentageFormatter(
  value: Value,
  _format?: NumberAbbreviationFormat,
  prefix?: string,
) {
  if (value === null || value === undefined) {
    return ""
  }

  return `${prefix || ""}${numeral(value).format("0,0[.]00")}%`
}

/** @deprecated */
export function ratioFormatter(value: Value, _format?: string, prefix?: string) {
  if (value === null || value === undefined) {
    return ""
  }
  return `${prefix || ""}${numeral(value).format("0,0.0")}x`
}

/** @deprecated */
export function numberFormatter(value: Value, format?: NumberAbbreviationFormat, prefix?: string) {
  if (value === null || value === undefined) {
    return ""
  }

  if (format === "consistent") {
    const times1000 = Number(value) / 1000
    return `${numeral(times1000).format(`${prefix ? prefix : ""}0,0`)} K`
  }

  const numeralFormat =
    format === "compact"
      ? `${prefix ? prefix : ""}0.0 a`
      : format || `${prefix ? prefix : ""}0[.]0 a`

  if (format === "full") {
    if (
      (Math.abs(Number(value)) >= 0.1 && Number(value) > 0) ||
      (Number(value) < 0 && Number(value) <= -0.1)
    ) {
      return numeral(value).format(`${prefix ? prefix : ""}0,0.0 a`)
    } else {
      return numeral(value).format(`${prefix ? prefix : ""}0,0[.]00000 a`)
    }
  }

  return numeral(value).format(numeralFormat)
}

/** @deprecated */
export function bytesFormatter(value: Value, _format?: string, prefix?: string) {
  if (value === null || value === undefined) {
    return ""
  }
  return `${prefix || ""}${numeral(value).format("0[.]0 b")}`
}

/** @deprecated */
export function timeFormatter(value: Value, _format?: string, prefix?: string) {
  if (typeof value === "undefined" || value === null) {
    return "-"
  }
  if (typeof value === "string") {
    // Should never be :thinking:
    return `${prefix || ""}${formatTime(Number.parseInt(value, 10))}`
  }
  return `${prefix || ""}${formatTime(value)}`
}

/** @deprecated */
export const thousandsFormatter = (value: Value, _format?: string, prefix?: string) => {
  if (!value && value !== 0) {
    return ""
  }

  // times value by 1000
  const times1000 = Number(value) / 1000

  return `${prefix || "$"}${numeral(times1000).format("0,0")} K`
}

function pricingFormatterWithDecimals(value: Value, format?: string, prefix?: string) {
  if (!value) {
    return `0`
  }
  if (canUseNumeral(value)) {
    if (format) {
      return numeral(value).format(format)
    }

    return numeral(value).format(Number(value) > 0.01 ? "$0.00 a" : "$0,0.00[00] a")
  }
  return `${prefix || "$"}${value}`
}

/** @deprecated */
export const formatters = {
  [SERIE_FORMAT.PRICE]: (value: Value, format?: string, prefix?: string) => {
    if (format === "compact" || format === "full" || format === "consistent") {
      return numberFormatter(value, format, prefix || "$")
    }
    return pricingFormatterWithDecimals(value, format, prefix)
  },
  [SERIE_FORMAT.RATIO]: ratioFormatter,
  [SERIE_FORMAT.PERCENTAGE]: percentageFormatter,
  [SERIE_FORMAT.NUMBER]: numberFormatter,
  [SERIE_FORMAT.TOKEN]: numberFormatter,
  [SERIE_FORMAT.TIME]: timeFormatter,
  [SERIE_FORMAT.BYTES]: bytesFormatter,
  [SERIE_FORMAT.TOKEN_ICP]: numberFormatter,
  [SERIE_FORMAT.TOKEN_CKBTC]: numberFormatter,
  [SERIE_FORMAT.TOKEN_CKETH]: numberFormatter,
  [SERIE_FORMAT.CYCLES_PER_SECONDS]: numberFormatter,
  [SERIE_FORMAT.KWH]: numberFormatter,
} as const

/** @deprecated */
export const formatNumber = (
  value: Value,
  format?: NumberAbbreviationFormat,
  metric?: { format: SERIE_FORMAT },
  prefix?: string,
) => {
  // // Check if format is one of the special formats
  // if (format === 'compact' || format === 'full' || format === 'consistent') {
  //   return numberFormatter(value, format, prefix)
  // }

  // Otherwise, pick the formatter based on the metric
  const formatter = formatters[metric?.format as SERIE_FORMAT]
  if (formatter) {
    return formatter(value, format, prefix)
  }
  return numberFormatter(value, format, prefix)
}

// Update the formatter functions to use formatterOptions
/** @deprecated */
export function createNumberFormatter(formatterOptions: FormatterOptions = {}) {
  return function (this: Point) {
    const { token, postfix, numberFormat } = formatterOptions
    const prefix = token ? `${token} ` : ""
    const value = this.y

    return numberFormatter(value, numberFormat, prefix) + (postfix || "")
  }
}

/** @deprecated */
export function createPriceFormatter(formatterOptions: FormatterOptions = {}) {
  return function (this: Point) {
    const { token, postfix, numberFormat } = formatterOptions
    const prefix = token ? `${token} ` : "$"
    const value = this.y

    if (numberFormat) {
      return numberFormatter(value, numberFormat, prefix) + (postfix || "")
    }

    return pricingFormatterWithDecimals(value, undefined, prefix) + (postfix || "")
  }
}

/** @deprecated */
export function createPercentageFormatter(formatterOptions: FormatterOptions = {}) {
  return function (this: Point) {
    const { token, postfix } = formatterOptions
    const prefix = token ? `${token} ` : ""
    const value = this.y

    return percentageFormatter(value, undefined, prefix) + (postfix || "")
  }
}

/** @deprecated */
export function createRatioFormatter(formatterOptions: FormatterOptions = {}) {
  return function (this: Point) {
    const { token, postfix } = formatterOptions
    const prefix = token ? `${token} ` : ""
    const value = this.y

    return ratioFormatter(value, undefined, prefix) + (postfix || "")
  }
}

/** @deprecated */
export function createTimeFormatter(formatterOptions: FormatterOptions = {}) {
  return function (this: Point) {
    const { token, postfix } = formatterOptions
    const prefix = token ? `${token} ` : ""
    const value = this.y

    return timeFormatter(value, undefined, prefix) + (postfix || "")
  }
}

/** @deprecated */
export function createBytesFormatter(formatterOptions: FormatterOptions = {}) {
  return function (this: Point) {
    const { token, postfix } = formatterOptions
    const prefix = token ? `${token} ` : ""
    const value = this.y

    return bytesFormatter(value, undefined, prefix) + (postfix || "")
  }
}
